// Keeps the post-submit confirmation across reloads, so a refresh doesn't drop the visitor back into
// an empty wizard (and invite a duplicate submission). sessionStorage on purpose: it holds the email
// and the confirmation token, and the confirmation only matters for this visit.

import type { QuoteEmailEnvelope } from '@/lib/api/leads';

export interface SavedConfirmation {
  fullName: string;
  email: string;
  /** false when the lead was saved but the quote email failed to send */
  emailSent: boolean;
  leadId: string;
  submittedAt: string;
  /** Lets this visitor resend the quote or fix the email; absent when the server could not issue one */
  confirmationToken?: string;
  quoteEmail?: QuoteEmailEnvelope;
  resendsLeft?: number;
  emailChangesLeft?: number;
  /** ISO time until which "Reenviar" stays disabled (mirrors the server cooldown, survives reloads) */
  cooldownUntil?: string;
  /** Instant quote variant: the event Mi boda opens, and which flow this lead got */
  quoteFlow?: 'instant' | 'email';
  eventId?: string;
  /** More guests than the quote covers: nothing was emailed, the next step is WhatsApp with this guest count */
  overCapacityGuests?: number;
}

const STORAGE_KEY = 'casa-aragon-wizard:confirmation';

export const loadConfirmation = (): SavedConfirmation | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedConfirmation;
    return typeof parsed.email === 'string' && typeof parsed.leadId === 'string' ? parsed : null;
  } catch {
    return null;
  }
};

export const saveConfirmation = (confirmation: SavedConfirmation) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(confirmation));
  } catch {
    // Storage unavailable (private mode, quota) - the confirmation just won't survive a reload
  }
};

export const clearConfirmation = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
